import { Head } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/Card'
import { AlertCircle, Clock, CheckCircle2, XCircle, Home } from 'lucide-react'
import appIcon from '@/assets/images/icon-128.png'
import { AuthBackground } from '@/components/auth/AuthBackground'

type InvalidReason = 'not_found' | 'expired' | 'already_accepted' | 'revoked'

interface InvalidProps {
  reason: InvalidReason
  account_name?: string
}

const reasonConfig: Record<InvalidReason, {
  icon: typeof AlertCircle
  title: string
  description: (accountName?: string) => string
  iconColor: string
}> = {
  not_found: {
    icon: AlertCircle,
    title: 'Invitation Not Found',
    description: () => 'This invitation link is invalid or has been removed. Please contact the person who invited you for a new link.',
    iconColor: 'text-destructive',
  },
  expired: {
    icon: Clock,
    title: 'Invitation Expired',
    description: (accountName) => `This invitation to join ${accountName || 'the team'} has expired. Please contact the team admin for a new invitation.`,
    iconColor: 'text-amber-400',
  },
  already_accepted: {
    icon: CheckCircle2,
    title: 'Already Accepted',
    description: (accountName) => `You've already accepted this invitation and are a member of ${accountName || 'this team'}. Head to your inbox to get started.`,
    iconColor: 'text-primary',
  },
  revoked: {
    icon: XCircle,
    title: 'Invitation Revoked',
    description: (accountName) => `This invitation to join ${accountName || 'the team'} has been cancelled by the team admin.`,
    iconColor: 'text-destructive',
  },
}

export default function Invalid({ reason, account_name }: InvalidProps) {
  const config = reasonConfig[reason] || reasonConfig.not_found
  const Icon = config.icon

  return (
    <>
      <Head title="Invalid Invitation" />

      <AuthBackground>
        <div className="w-full max-w-md">
          <Card className="w-full">
            <CardHeader className="text-center">
              <img src={appIcon} alt="Banshee" className="size-16 mx-auto mb-4 rounded-xl" />
              <div className="flex justify-center mb-4">
                <div className={`size-16 rounded-full bg-muted flex items-center justify-center`}>
                  <Icon className={`size-8 ${config.iconColor}`} />
                </div>
              </div>
              <CardTitle>{config.title}</CardTitle>
              <CardDescription>
                {config.description(account_name)}
              </CardDescription>
            </CardHeader>

            <CardContent>
              {reason === 'already_accepted' && (
                <Button
                  onClick={() => window.location.href = '/'}
                  className="w-full cursor-pointer"
                  size="lg"
                >
                  <Home className="size-4 mr-2" />
                  Go to Inbox
                </Button>
              )}

              {reason !== 'already_accepted' && (
                <Button
                  onClick={() => window.location.href = '/'}
                  variant="outline"
                  className="w-full cursor-pointer"
                  size="lg"
                >
                  <Home className="size-4 mr-2" />
                  Go to Homepage
                </Button>
              )}
            </CardContent>

            <CardFooter className="justify-center">
              <p className="text-xs text-muted-foreground text-center">
                Need help? Contact support at{' '}
                <a href="mailto:support@banshee.chat" className="text-primary hover:underline">
                  support@banshee.chat
                </a>
              </p>
            </CardFooter>
          </Card>
        </div>
      </AuthBackground>
    </>
  )
}
